import React from 'react';
import { TrendingUp, Settings, Bike, Shield } from 'lucide-react';

type Role = 'seller' | 'manager' | 'motoboy' | 'admin';

interface UserAvatarProps {
  name: string;
  role: Role;
  imageUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

const ROLE_CONFIG: Record<Role, {
  label: string;
  Icon: React.ElementType;
  bg: string;
  iconColor: string;
  ring: string;
  initials: string;
}> = {
  seller: {
    label: 'Vendedor',
    Icon: TrendingUp,
    bg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    ring: 'ring-blue-200',
    initials: 'bg-blue-600',
  },
  manager: {
    label: 'Gestor',
    Icon: Settings,
    bg: 'bg-slate-100',
    iconColor: 'text-slate-700',
    ring: 'ring-slate-300',
    initials: 'bg-slate-700',
  },
  motoboy: {
    label: 'Motoboy',
    Icon: Bike,
    bg: 'bg-orange-50',
    iconColor: 'text-orange-500',
    ring: 'ring-orange-200',
    initials: 'bg-orange-500',
  },
  admin: {
    label: 'Administrador',
    Icon: Shield,
    bg: 'bg-red-50',
    iconColor: 'text-red-600',
    ring: 'ring-red-200',
    initials: 'bg-red-600',
  },
};

const SIZE = {
  sm: { wrap: 'w-8 h-8', icon: 'w-3.5 h-3.5', text: 'text-[10px]', badge: 'w-3 h-3 -bottom-0.5 -right-0.5', badgeIcon: 'w-1.5 h-1.5' },
  md: { wrap: 'w-10 h-10', icon: 'w-4 h-4', text: 'text-xs', badge: 'w-4 h-4 -bottom-1 -right-1', badgeIcon: 'w-2 h-2' },
  lg: { wrap: 'w-14 h-14', icon: 'w-5 h-5', text: 'text-sm', badge: 'w-5 h-5 -bottom-1 -right-1', badgeIcon: 'w-2.5 h-2.5' },
};

export default function UserAvatar({
  name,
  role,
  imageUrl,
  size = 'md',
  showTooltip = true,
}: UserAvatarProps) {
  const cfg = ROLE_CONFIG[role] ?? ROLE_CONFIG.seller;
  const s = SIZE[size];
  const initials = name ? name.slice(0, 2).toUpperCase() : '??';
  const { Icon } = cfg;

  return (
    <div
      className="relative inline-flex flex-shrink-0 group"
      title={showTooltip ? cfg.label : undefined}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          className={`${s.wrap} rounded-full object-cover ring-2 ${cfg.ring}`}
        />
      ) : (
        <div
          className={`
            ${s.wrap} rounded-full flex items-center justify-center
            ${cfg.bg} ring-1 ${cfg.ring} shadow-sm
            transition-all duration-150 group-hover:shadow-md group-hover:ring-2
          `}
        >
          <span className={`font-bold ${cfg.iconColor} ${s.text} leading-none select-none`}>
            {initials}
          </span>
        </div>
      )}

      {/* Role badge */}
      <span
        className={`
          absolute ${s.badge} rounded-full flex items-center justify-center
          ${cfg.initials} ring-2 ring-white shadow-sm
        `}
        aria-hidden="true"
      >
        <Icon className={`${s.badgeIcon} text-white`} />
      </span>
    </div>
  );
}
